// Deterministic mock schedule for the RCM "insurance readiness" calendar
// (rcm-22 Day + rcm-23 Week). Client-side only: a seeded PRNG produces the same
// schedule on every reload so the Day grid, the Week agenda, the counts in the
// band and the visit modal all read one source of truth. Real data comes from
// the PMS sync + ELVA's verification snapshots (rcm-22 §7); this only exists so
// the view can be demoed over mock data.
//
// MOCK_NOW is 2026-10-16 (a Friday) — the practice's real "today" (the staging
// bug showed May; rcm-22 §6). The schedule spans the current week (Mon Oct 12 –
// Fri Oct 16) and next week (Mon Oct 19 – Fri Oct 23). Weekends are closed.

import { MOCK_NOW, rng, fullName, PAYERS } from '@/components/lists';
import { PMS } from '@/features/shared/pms';

/** Where a visit stands on the one question: is its insurance ready? */
export type ReadinessState = 'ready' | 'not_ready' | 'checking';

/** One procedure line in a visit's estimate (same engine as the patient page). */
export interface EstimateLine {
  label: string;
  cdt?: string;
  fee: number;
  /** The payer's allowed amount for the line (fee schedule). */
  allowed: number;
  /** 'known' shows amounts; 'pending' shows "Not yet known"; 'ended' shows no estimate. */
  mode: 'known' | 'pending' | 'ended';
  insurance: number;
  patient: number;
}

/** The amber "not ready" bar in the visit modal: a sentence, what's next, one link. */
export interface NotReadyBox {
  heading: string;
  detail: string;
  /** Where it's decided — never a staff action ELVA does itself (rcm-22 §4, D104). */
  link: { label: string; to: string };
}

/** What ELVA already knows before the day-before check (week view "Known now"). */
export interface KnownNow {
  kind: 'not_ready' | 'worth_knowing';
  /** The reason, in words. */
  reason: string;
}

/** A benefit or practice opportunity for one visit (rcm-22 §4). */
export interface VisitOpportunity {
  id: string;
  name: string;
  /** One explaining line under the name. */
  line: string;
  /** 'plan': what the plan would pay, capped by what it has left. 'worth': a value, not a plan payment. */
  kind: 'plan' | 'worth';
  /** Which pool caps a plan amount. */
  pool: 'yearly' | 'ortho' | 'none';
  amount: number;
  /** How much the right-hand "coverage" figure is worth chasing. Derived when absent. */
  priority?: 'high' | 'medium' | 'low';
  /** Age limits, applied before anything is shown (D27). */
  ageMin?: number;
  ageMax?: number;
  /** Behind "Not now": why it isn't offered at this visit. */
  notNow: string;
}

/** A family member on the same plan (rcm-22 §4, "Others on this plan"). */
export interface FamilyMember {
  name: string;
  relation: string;
  /** Chart number, used for /patients/:id. */
  chart: string;
  /** One insurance-relevant fact about this person. */
  fact: string;
  yearlyMaxLeft: number;
  /** Denominator for the benefits-remaining bar; defaults to 1500 when absent. */
  benefitsMax?: number;
  /** Recall status, for the family table. */
  recall?: { label: string; tone: 'ok' | 'soon' | 'overdue' };
  /** Unscheduled treatment still in the plan. */
  unscheduled?: { amount: number; items: number };
}

/** When the yearly maximum bites: what the plan would have paid, and the uncapped patient share. */
export interface CapNote {
  wouldPay: number;
  left: number;
  uncappedPatient: number;
}

export interface PlanYear {
  /** "Jan 1 – Dec 31". */
  range: string;
  /** "2026". */
  year: string;
}

export interface DeductibleFact {
  /** "Met" or "$50 left". */
  label: string;
  /** "$50, met Mar 3" or "of $50". */
  sub: string;
}

export interface Visit {
  id: string;
  /** ISO yyyy-mm-dd. */
  dateIso: string;
  providerId: string;
  /** Minutes from midnight (8:00 = 480). */
  startMin: number;
  /** Length in minutes (60 or 90). */
  lengthMin: number;
  patient: string;
  age: number;
  /** Year the patient joined the practice. */
  since: number;
  /** US date, mm/dd/yyyy. */
  dob: string;
  chart: string;
  procedure: { label: string; cdt?: string };
  state: ReadinessState;
  /** The one-line state sentence on the grid block. */
  line: string;
  /** "Delta Dental of Texas · PPO", or null when there's no insurance on file. */
  plan: string | null;
  /** Short payer name for column heads and sentences ("Delta Dental"). */
  payer: string | null;
  member: string | null;
  /** When ELVA last checked, as a local ISO datetime ("2026-10-15T06:10:00"); null while checking or never run. */
  checkedAt: string | null;
  /** The last check failed. "Check again" then shows whatever its age. */
  checkFailed: boolean;
  /** Number of sources the check used. */
  sources: number | null;
  yearlyMaxLeft: number | null;
  yearlyMax: number | null;
  /** Orthodontic maximum: what's left and the total. */
  ortho: { left: number; max: number } | null;
  deductible: DeductibleFact | null;
  planYear: PlanYear | null;
  estimate: EstimateLine[];
  estimateNote?: string;
  /** For an ended plan: the coverage-end date, in words ("Sep 30, 2025"). */
  coverageEnded?: string;
  /** Set when the yearly maximum bites (D47). */
  cap?: CapNote;
  notReady?: NotReadyBox;
  known?: KnownNow;
  opportunities: VisitOpportunity[];
  family: FamilyMember[];
}

export interface Provider {
  id: string;
  name: string;
  kind: 'provider' | 'hygiene';
}

/** Providers + hygiene chairs, in PMS order (rcm-22 §3). */
export const PROVIDERS: Provider[] = [
  { id: 'carter', name: 'Dr. Emily Carter', kind: 'provider' },
  { id: 'poe', name: 'Dr. Raymond Poe', kind: 'provider' },
  { id: 'moreno', name: 'Hygiene · Lisa Moreno', kind: 'hygiene' },
  { id: 'tran', name: 'Hygiene · Kim Tran', kind: 'hygiene' },
];

/** The practice has one location in the mock, so the location Select is hidden. */
export const LOCATIONS = ['Alamo Heights Dental'];

/** Opportunities the practice has hidden in Settings (shown as a foot line in the modal). */
export const SETTINGS_HIDDEN_OPPORTUNITIES = ['night guard', 'whitening'];

/* ----------------------------------------------------------------------------
 * Dates — all computed from MOCK_NOW so nothing is hard-coded to a wall clock.
 * -------------------------------------------------------------------------- */

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WD_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MO_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function parseIso(iso: string): Date {
  const parts = iso.split('-').map((n) => Number(n));
  const y = parts[0] ?? 2026;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

export function isoOf(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${String(y)}-${m}-${day}`;
}

/** Parses a local ISO datetime ("2026-10-15T06:10:00") without relying on Date's string rules. */
export function parseStamp(iso: string): Date {
  const [datePart = '', timePart = ''] = iso.split('T');
  const day = parseIso(datePart);
  const [h = 0, mi = 0] = timePart.split(':').map((n) => Number(n));
  day.setHours(h, mi, 0, 0);
  return day;
}

/** The mock practice "today", ISO. */
export const MOCK_TODAY_ISO = isoOf(MOCK_NOW);
/** Minutes-from-midnight of the mock "now" (for the Day grid now-line). */
export const MOCK_NOW_MIN = MOCK_NOW.getHours() * 60 + MOCK_NOW.getMinutes();

/** Monday of the week that contains `iso`. */
export function mondayOf(iso: string): string {
  const d = parseIso(iso);
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  const delta = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + delta);
  return isoOf(d);
}

export const THIS_WEEK_MONDAY = mondayOf(MOCK_TODAY_ISO);
export const NEXT_WEEK_MONDAY = addDaysIso(THIS_WEEK_MONDAY, 7);

export function addDaysIso(iso: string, days: number): string {
  const d = parseIso(iso);
  d.setDate(d.getDate() + days);
  return isoOf(d);
}

/** "Fri, Oct 16" */
export function labelDayShort(iso: string): string {
  const d = parseIso(iso);
  return `${WD_SHORT[d.getDay()] ?? ''}, ${MO_SHORT[d.getMonth()] ?? ''} ${String(d.getDate())}`;
}

/** "Friday, October 16" style long day, used sparingly. */
export function labelDayLong(iso: string): string {
  const d = parseIso(iso);
  return `${WD_LONG[d.getDay()] ?? ''}, ${MO_SHORT[d.getMonth()] ?? ''} ${String(d.getDate())}`;
}

/** "Oct 19 – 23" (same month) or "Oct 29 – Nov 2". */
export function labelWeekRange(mondayIso: string): string {
  const mon = parseIso(mondayIso);
  const fri = parseIso(addDaysIso(mondayIso, 4));
  const lhs = `${MO_SHORT[mon.getMonth()] ?? ''} ${String(mon.getDate())}`;
  const rhs =
    mon.getMonth() === fri.getMonth()
      ? String(fri.getDate())
      : `${MO_SHORT[fri.getMonth()] ?? ''} ${String(fri.getDate())}`;
  return `${lhs} – ${rhs}`;
}

/* ----------------------------------------------------------------------------
 * Time formatting (12h, mono).
 * -------------------------------------------------------------------------- */

/** 480 → "8:00", 780 → "1:00" (no meridiem, for grid blocks + week rows). */
export function blockTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12)}:${String(m).padStart(2, '0')}`;
}

/** 780 → "1:00 PM" (modal kicker and facts). */
export function clockTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const h12 = ((h + 11) % 12) + 1;
  const mer = h < 12 ? 'AM' : 'PM';
  return `${String(h12)}:${String(m).padStart(2, '0')} ${mer}`;
}

/** 480 → "8 AM" (grid hour labels). */
export function hourLabel(min: number): string {
  const h = Math.floor(min / 60);
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12)} ${h < 12 ? 'AM' : 'PM'}`;
}

/** Grid window: 8 AM – 5 PM. */
export const GRID_START_MIN = 8 * 60;
export const GRID_END_MIN = 17 * 60;
export const PX_PER_HOUR = 64;

/* ----------------------------------------------------------------------------
 * Money.
 * -------------------------------------------------------------------------- */

/** Whole dollars: "$1,500" (opportunities and plan cards). */
export function usdWhole(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/** What the plan pays and the patient owes once the yearly maximum is applied (D47). */
export interface CappedEstimate {
  fee: number;
  allowed: number;
  plan: number;
  patient: number;
}

/**
 * The single place the cap is applied. Lines are quoted before the cap; the
 * plan can pay no more than what the yearly maximum has left, and the patient
 * owes the rest of the allowed amount. The modal and the printed report both
 * read this, so their figures cannot differ.
 */
export function cappedTotals(lines: readonly EstimateLine[], cap: CapNote | null): CappedEstimate {
  let fee = 0;
  let allowed = 0;
  let plan = 0;
  for (const l of lines) {
    fee += l.fee;
    allowed += l.allowed;
    plan += l.insurance;
  }
  const paid = cap ? Math.min(plan, cap.left) : plan;
  return { fee, allowed, plan: paid, patient: allowed - paid };
}

/** Cents: "$532.00" (the estimate). */
export function usdCents(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/* ----------------------------------------------------------------------------
 * Last check: "yesterday, 6:10 AM", and whether it is over 24 hours old (D22/D35).
 * -------------------------------------------------------------------------- */

/** "today, 6:10 AM" / "yesterday, 6:10 AM" / "Oct 12, 6:10 AM", relative to the mock today. */
export function checkWhen(iso: string): string {
  const d = parseStamp(iso);
  const dayIso = isoOf(d);
  const diff = Math.round((parseIso(MOCK_TODAY_ISO).getTime() - parseIso(dayIso).getTime()) / 86_400_000);
  const day = diff === 0 ? 'today' : diff === 1 ? 'yesterday' : `${MO_SHORT[d.getMonth()] ?? ''} ${String(d.getDate())}`;
  return `${day}, ${clockTime(d.getHours() * 60 + d.getMinutes())}`;
}

/** True when the last check is more than 24 hours before the mock now. */
export function checkIsStale(iso: string): boolean {
  return MOCK_NOW.getTime() - parseStamp(iso).getTime() > 24 * 60 * 60 * 1000;
}

/* ----------------------------------------------------------------------------
 * The schedule.
 * -------------------------------------------------------------------------- */

const SLOTS = [480, 510, 540, 570, 600, 630, 660, 690, 720, 750, 780, 810, 840, 870, 900, 930, 960, 990];

const READY_PROCS: { label: string; cdt?: string; len: number }[] = [
  { label: 'Cleaning', cdt: 'D1110', len: 60 },
  { label: 'Exam + BW', cdt: 'D0120', len: 60 },
  { label: 'Perio maint.', cdt: 'D4910', len: 60 },
  { label: 'Filling #3 O', cdt: 'D2392', len: 60 },
  { label: 'Filling #14', cdt: 'D2391', len: 60 },
  { label: 'Crown #19 seat', cdt: 'D2740', len: 90 },
  { label: 'Root canal #19', cdt: 'D3330', len: 90 },
  { label: 'Exam + FMX', cdt: 'D0210', len: 60 },
  { label: 'SRP 2 quads', cdt: 'D4341', len: 90 },
  { label: 'Crown #2 prep', cdt: 'D2740', len: 90 },
];

/** Plan year shared by the mock plans. */
const PLAN_YEAR: PlanYear = { range: 'Jan 1 – Dec 31', year: '2026' };

/** Opportunities a filler visit can draw on. Age limits and the plan's remaining amounts are applied in visibleOpportunities(). */
const FILLER_OPPORTUNITIES: VisitOpportunity[] = [
  {
    id: 'aligners',
    name: 'Clear aligners',
    line: 'Adult ortho is on this plan, and the orthodontic maximum is unused.',
    kind: 'plan',
    pool: 'ortho',
    amount: 1500,
    ageMin: 13,
    notNow: 'Asked about straightening at a past visit. Not decided.',
  },
  {
    id: 'sealants',
    name: 'Sealants, back molars',
    line: 'Covered to age 16 on this plan.',
    kind: 'plan',
    pool: 'yearly',
    amount: 180,
    ageMax: 16,
    notNow: 'Waits for the next cleaning.',
  },
  {
    id: 'fluoride',
    name: 'Fluoride varnish',
    line: 'Covered twice a year to age 14.',
    kind: 'plan',
    pool: 'yearly',
    amount: 60,
    ageMax: 14,
    notNow: 'Waits for the next cleaning.',
  },
  {
    id: 'crown-planned',
    name: 'Crown, planned and not booked',
    line: 'Still in the treatment plan from the chart.',
    kind: 'plan',
    pool: 'yearly',
    amount: 1100,
    notNow: 'The patient has not booked it yet.',
  },
];

/** The unused yearly maximum, as a worth line (not a plan payment). */
function unusedMaxOpportunity(maxLeft: number): VisitOpportunity {
  return {
    id: 'unused-max',
    name: `${usdWhole(maxLeft)} of this year's maximum is unused`,
    line: 'Resets Jan 1. Use it before it is gone.',
    kind: 'worth',
    pool: 'none',
    amount: maxLeft,
    notNow: 'Not booked yet; the next visit can use it.',
  };
}

function seedFromIso(iso: string): number {
  let h = 2166136261;
  for (let i = 0; i < iso.length; i++) {
    h ^= iso.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function dobFromAge(r: () => number, age: number): string {
  const year = MOCK_NOW.getFullYear() - age;
  const month = Math.floor(r() * 12) + 1;
  const day = Math.floor(r() * 27) + 1;
  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(year)}`;
}

function chartNo(r: () => number): string {
  return String(10000 + Math.floor(r() * 89999));
}

function memberId(r: () => number, initials: string): string {
  const a = String(1000 + Math.floor(r() * 8999));
  const b = String(1000 + Math.floor(r() * 8999));
  return `${initials}-${a}-${b}`;
}

function initialsOf(name: string): string {
  const parts = name.split(' ');
  const f = parts[0]?.[0] ?? 'X';
  const l = parts[1]?.[0] ?? 'X';
  return `${f}${l}`;
}

/** A ready / still-checking filler visit. Never "not ready" — those are featured. */
function makeFiller(
  r: () => number,
  dateIso: string,
  providerId: string,
  startMin: number,
  lengthMin: number,
  idx: number,
): Visit {
  const name = fullName(r);
  const age = 20 + Math.floor(r() * 60);
  const pool = READY_PROCS.filter((p) => p.len === lengthMin);
  const base = pool.length > 0 ? pool : READY_PROCS;
  const fallbackProc = READY_PROCS[0] ?? { label: 'Visit', len: 60 };
  const proc = base[Math.min(base.length - 1, Math.floor(r() * base.length))] ?? fallbackProc;
  const payer = PAYERS[Math.floor(r() * PAYERS.length)] ?? 'Aetna';
  const planType = r() < 0.5 ? 'PPO' : 'DPPO';
  const checking = r() < 0.06;

  // Fillers stay under the yearly max: fee $120–$780, share at most ~$330.
  const fee = 120 + Math.floor(r() * 12) * 60;
  const allowed = fee - Math.round(fee * 0.15);
  const covered = r() < 0.45;
  const insurance = covered ? allowed : Math.round(allowed / 2);
  const patient = allowed - insurance;
  const maxLeft = 800 + Math.floor(r() * 8) * 100; // $800–$1,500
  const fresh = r() < 0.25;
  const checkFailed = !checking && r() < 0.04;
  const since = 2010 + Math.floor(r() * 16);

  const opportunities: VisitOpportunity[] = [];
  for (const o of FILLER_OPPORTUNITIES) {
    if (r() < 0.4) opportunities.push(o);
  }
  if (r() < 0.3) opportunities.push(unusedMaxOpportunity(maxLeft));

  const procLabel = proc.label;
  const procedure = proc.cdt !== undefined ? { label: procLabel, cdt: proc.cdt } : { label: procLabel };
  const estimateLine: EstimateLine = {
    label: procLabel,
    ...(proc.cdt !== undefined ? { cdt: proc.cdt } : {}),
    fee,
    allowed,
    mode: 'known',
    insurance,
    patient,
  };
  const id = `v-${dateIso}-${providerId}-${String(startMin)}-${String(idx)}`;
  const plan = `${payer} ${planType}`;
  const member = memberId(r, initialsOf(name));
  const dob = dobFromAge(r, age);
  const chart = chartNo(r);
  const common = {
    id,
    dateIso,
    providerId,
    startMin,
    lengthMin,
    patient: name,
    age,
    since,
    dob,
    chart,
    procedure,
    plan,
    payer,
    member,
  };

  if (checking) {
    return {
      ...common,
      state: 'checking',
      line: 'Checking now: plan found, benefits pending',
      checkedAt: null,
      checkFailed: false,
      sources: null,
      yearlyMaxLeft: null,
      yearlyMax: null,
      ortho: null,
      deductible: null,
      planYear: null,
      estimate: [{ ...estimateLine, mode: 'pending', insurance: 0, patient: 0 }],
      estimateNote: 'Amounts appear when the benefits check finishes — usually within the hour.',
      opportunities: [],
      family: [],
    };
  }

  return {
    ...common,
    state: 'ready',
    line: patient > 0 ? `✓ Covered · patient ${usdWhole(patient)}` : '✓ Covered in full',
    checkedAt: fresh ? '2026-10-16T06:10:00' : '2026-10-15T06:10:00',
    checkFailed,
    sources: 2 + Math.floor(r() * 3),
    yearlyMaxLeft: maxLeft,
    yearlyMax: 1500,
    ortho: { left: 1500, max: 1500 },
    deductible: r() < 0.5 ? { label: 'Met', sub: 'met for 2026' } : { label: '$50 left', sub: 'of $50' },
    planYear: PLAN_YEAR,
    estimate: [estimateLine],
    opportunities,
    family: [],
  };
}

/**
 * Featured visits — the ones ELVA already knows something about (not ready,
 * worth knowing, or the demo visit). What a featured visit states; featured()
 * fills the rest with plan defaults.
 */
interface FeaturedInput extends Partial<Omit<Visit, 'id'>> {
  dateIso: string;
  providerId: string;
  startMin: number;
  lengthMin: number;
  patient: string;
  age: number;
  dob: string;
  chart: string;
  procedure: { label: string; cdt?: string };
  state: ReadinessState;
  line: string;
  estimate: EstimateLine[];
}

function featured(v: FeaturedInput): Omit<Visit, 'id'> {
  const plan = v.plan ?? null;
  const firstWord = plan === null ? null : (plan.split(' ')[0] ?? null);
  return {
    dateIso: v.dateIso,
    providerId: v.providerId,
    startMin: v.startMin,
    lengthMin: v.lengthMin,
    patient: v.patient,
    age: v.age,
    since: v.since ?? 2019,
    dob: v.dob,
    chart: v.chart,
    procedure: v.procedure,
    state: v.state,
    line: v.line,
    plan,
    payer: v.payer ?? firstWord,
    member: v.member ?? null,
    checkedAt: v.checkedAt ?? null,
    checkFailed: v.checkFailed ?? false,
    sources: v.sources ?? null,
    yearlyMaxLeft: v.yearlyMaxLeft ?? null,
    yearlyMax: v.yearlyMax ?? null,
    ortho: v.ortho ?? null,
    deductible: v.deductible ?? null,
    planYear: v.planYear !== undefined ? v.planYear : plan === null ? null : PLAN_YEAR,
    estimate: v.estimate,
    opportunities: v.opportunities ?? [],
    family: v.family ?? [],
    ...(v.estimateNote !== undefined ? { estimateNote: v.estimateNote } : {}),
    ...(v.coverageEnded !== undefined ? { coverageEnded: v.coverageEnded } : {}),
    ...(v.cap !== undefined ? { cap: v.cap } : {}),
    ...(v.notReady !== undefined ? { notReady: v.notReady } : {}),
    ...(v.known !== undefined ? { known: v.known } : {}),
  };
}

const PREAUTH_LINK = { label: 'Decide in Pre-auths', to: '/pre-auths' };
const HITL_LINK = { label: 'Open in Human in the loop', to: '/human-in-the-loop' };

const FEATURED: Record<string, Omit<Visit, 'id'>[]> = {
  // ---- This week ----
  [addDaysIso(THIS_WEEK_MONDAY, 1)]: [
    // Tue Oct 13 — "Plan not found"
    featured({
      dateIso: addDaysIso(THIS_WEEK_MONDAY, 1),
      providerId: 'poe',
      startMin: 540,
      lengthMin: 90,
      patient: 'Gregory Pine',
      age: 57,
      since: 2014,
      dob: '07/22/1969',
      chart: '19022',
      procedure: { label: 'Crown #14', cdt: 'D2740' },
      state: 'not_ready',
      line: 'Plan not found',
      plan: 'Cigna DPPO',
      payer: 'Cigna',
      member: 'GP-7781-2240',
      checkedAt: '2026-10-12T06:10:00',
      sources: 3,
      planYear: null,
      estimate: [{ label: 'Crown #14', cdt: 'D2740', mode: 'pending', fee: 1150, allowed: 960, insurance: 0, patient: 0 }],
      estimateNote: 'No estimate until the plan is confirmed.',
      notReady: {
        heading: "ELVA couldn't find this plan",
        detail: "The member ID didn't match any plan at Cigna. A teammate can check the card at check-in.",
        link: HITL_LINK,
      },
      known: { kind: 'not_ready', reason: 'Plan not found' },
    }),
  ],
  [addDaysIso(THIS_WEEK_MONDAY, 3)]: [
    // Thu Oct 15 — "Needs a person"
    featured({
      dateIso: addDaysIso(THIS_WEEK_MONDAY, 3),
      providerId: 'moreno',
      startMin: 840,
      lengthMin: 90,
      patient: 'Donna Schaefer',
      age: 63,
      since: 2011,
      dob: '11/03/1963',
      chart: '20881',
      procedure: { label: 'SRP 2 quads', cdt: 'D4341' },
      state: 'not_ready',
      line: 'Needs a person',
      plan: 'Guardian PPO',
      payer: 'Guardian',
      member: 'DS-3321-9087',
      checkedAt: '2026-10-14T06:10:00',
      sources: 4,
      estimate: [
        { label: 'SRP, lower right', cdt: 'D4341', mode: 'pending', fee: 480, allowed: 400, insurance: 0, patient: 0 },
        { label: 'SRP, lower left', cdt: 'D4341', mode: 'pending', fee: 480, allowed: 400, insurance: 0, patient: 0 },
      ],
      estimateNote: 'ELVA could not confirm the benefit on its own.',
      notReady: {
        heading: 'This one needs a person',
        detail: 'ELVA tried every automated check and still could not confirm scaling-and-root-planing coverage. A teammate can call Guardian.',
        link: HITL_LINK,
      },
      known: { kind: 'not_ready', reason: 'Needs a person' },
    }),
  ],
  // ---- Friday Oct 16 — "today" (the rcm-22 Day scene): 3 not ready + 1 checking + the demo visit ----
  [MOCK_TODAY_ISO]: [
    // 11:00 AM — the demo visit: a ready visit where the yearly maximum bites (D47).
    featured({
      dateIso: MOCK_TODAY_ISO,
      providerId: 'carter',
      startMin: 660,
      lengthMin: 60,
      patient: 'Jorge Martinez',
      age: 46,
      since: 2019,
      dob: '05/17/1980',
      chart: '15530',
      procedure: { label: 'Crown #14 seat', cdt: 'D2740' },
      state: 'ready',
      line: '✓ Covered · patient $532',
      plan: 'Delta Dental of Texas · PPO',
      payer: 'Delta Dental',
      member: '81447302',
      checkedAt: '2026-10-15T06:10:00',
      sources: 4,
      yearlyMaxLeft: 300,
      yearlyMax: 1500,
      ortho: { left: 1500, max: 1500 },
      deductible: { label: 'Met', sub: '$50 · Mar 3' },
      // Lines are quoted before the yearly maximum, as the rcm-22 table shows them.
      // The cap is applied once, in cappedTotals(), for the hero and the cap note.
      estimate: [
        { label: 'Crown, porcelain · #14', cdt: 'D2740', fee: 850, allowed: 720, mode: 'known', insurance: 360, patient: 360 },
        { label: 'X-ray, periapical', cdt: 'D0220', fee: 31, allowed: 28, mode: 'known', insurance: 28, patient: 0 },
        { label: 'Core build-up · #14', cdt: 'D2950', fee: 100, allowed: 84, mode: 'known', insurance: 42, patient: 42 },
      ],
      cap: { wouldPay: 430, left: 300, uncappedPatient: 402 },
      opportunities: [
        {
          id: 'aligners',
          name: 'Clear aligners',
          line: 'Delta pays a lifetime orthodontic benefit and none of it has been used.',
          kind: 'plan',
          pool: 'ortho',
          amount: 1500,
          priority: 'high',
          ageMin: 13,
          notNow: 'Asked about straightening in March (chart note). Not decided.',
        },
        {
          id: 'crown-14',
          name: 'Crown on #14, planned Mar 3, never booked',
          line: `Still in the treatment plan in ${PMS}. Same tooth group as today's crown.`,
          kind: 'plan',
          pool: 'yearly',
          amount: 360,
          priority: 'high',
          notNow: 'Still in the treatment plan; the patient has not booked it.',
        },
        {
          id: 'max-unused',
          name: "$300 of this year's maximum is unused",
          line: "Delta resets it on Jan 1. Today's visit uses all of it.",
          kind: 'worth',
          pool: 'none',
          amount: 300,
          priority: 'medium',
          notNow: 'Resets Jan 1; this visit uses it first.',
        },
      ],
      family: [
        {
          name: 'Sofia Martinez',
          relation: 'Spouse',
          chart: '15531',
          fact: 'cleaning due Nov 2, not booked',
          yearlyMaxLeft: 1180,
          benefitsMax: 1500,
          recall: { label: 'Due Nov 2', tone: 'soon' },
          unscheduled: { amount: 1250, items: 2 },
        },
        {
          name: 'Mateo Martinez',
          relation: 'Child',
          chart: '15532',
          fact: 'sealants covered to age 16, he is 11',
          yearlyMaxLeft: 1500,
          benefitsMax: 1500,
          recall: { label: 'On schedule', tone: 'ok' },
          unscheduled: { amount: 340, items: 1 },
        },
      ],
    }),
    // 1:00 PM — pre-auth with no answer yet.
    featured({
      dateIso: MOCK_TODAY_ISO,
      providerId: 'carter',
      startMin: 780,
      lengthMin: 90,
      patient: 'Mary Vincent',
      age: 61,
      since: 2014,
      dob: '03/14/1965',
      chart: '20417',
      procedure: { label: 'Implant #30', cdt: 'D6010' },
      state: 'not_ready',
      line: 'Pre-auth: no answer yet',
      plan: 'MetLife PDP+',
      payer: 'MetLife',
      member: 'MV-5512-0183',
      checkedAt: '2026-10-15T06:10:00',
      sources: 4,
      yearlyMaxLeft: 1120,
      yearlyMax: 1500,
      ortho: { left: 1500, max: 1500 },
      deductible: { label: 'Met', sub: 'met for 2026' },
      estimate: [{ label: 'Implant #30', cdt: 'D6010', mode: 'pending', fee: 2400, allowed: 1650, insurance: 0, patient: 0 }],
      estimateNote: 'Insurance is "not yet known" until MetLife answers the pre-auth.',
      notReady: {
        heading: "MetLife hasn't answered the pre-auth",
        detail: 'Sent Oct 6. MetLife usually answers in about 3 weeks, so around Oct 27 — after today’s visit.',
        link: PREAUTH_LINK,
      },
      known: { kind: 'not_ready', reason: 'Pre-auth sent Oct 6 · no answer yet' },
      opportunities: [
        {
          id: 'max-unused',
          name: "$1,120 of this year's maximum is unused",
          line: 'Resets Jan 1. The implant would use part of it.',
          kind: 'worth',
          pool: 'none',
          amount: 1120,
          notNow: 'Waits for the pre-auth answer.',
        },
      ],
    }),
    // 12:00 PM — the plan ended on Sep 30.
    featured({
      dateIso: MOCK_TODAY_ISO,
      providerId: 'poe',
      startMin: 720,
      lengthMin: 60,
      patient: 'Patricia Levin',
      age: 58,
      since: 2016,
      dob: '09/02/1968',
      chart: '18740',
      procedure: { label: 'Crown #30', cdt: 'D2740' },
      state: 'not_ready',
      line: 'Coverage ended Sep 30',
      plan: 'Delta Dental PPO',
      payer: 'Delta Dental',
      member: 'PL-2290-7781',
      checkedAt: '2026-10-15T06:10:00',
      sources: 3,
      planYear: null,
      coverageEnded: 'Sep 30, 2025',
      estimate: [{ label: 'Crown #30', cdt: 'D2740', mode: 'ended', fee: 1150, allowed: 960, insurance: 0, patient: 0 }],
      notReady: {
        heading: 'Her Delta Dental plan ended September 30',
        detail: 'The last check found no active coverage for today. She may have a new plan — a teammate can ask at check-in.',
        link: HITL_LINK,
      },
      known: { kind: 'not_ready', reason: 'Coverage ended Sep 30' },
    }),
    // 1:00 PM — no insurance on file.
    featured({
      dateIso: MOCK_TODAY_ISO,
      providerId: 'moreno',
      startMin: 780,
      lengthMin: 60,
      patient: 'Orville Allison',
      age: 49,
      since: 2021,
      dob: '05/19/1977',
      chart: '21055',
      procedure: { label: 'Cleaning', cdt: 'D1110' },
      state: 'not_ready',
      line: 'No insurance on file',
      plan: null,
      member: null,
      estimate: [{ label: 'Cleaning', cdt: 'D1110', mode: 'pending', fee: 120, allowed: 102, insurance: 0, patient: 0 }],
      estimateNote: 'No plan on file — ELVA has nothing to verify.',
      notReady: {
        heading: 'No insurance on file',
        detail: "ELVA couldn't find a plan to check. A teammate can add insurance, or confirm the visit is self-pay.",
        link: HITL_LINK,
      },
      known: { kind: 'not_ready', reason: 'No insurance on file' },
    }),
    // 11:00 AM — the benefits check is still running.
    featured({
      dateIso: MOCK_TODAY_ISO,
      providerId: 'tran',
      startMin: 660,
      lengthMin: 60,
      patient: 'Lester Connolly',
      age: 44,
      since: 2018,
      dob: '01/27/1982',
      chart: '20903',
      procedure: { label: 'Perio maint.', cdt: 'D4910' },
      state: 'checking',
      line: 'Checking now: plan found, benefits pending',
      plan: 'UnitedHealthcare DPPO',
      payer: 'UnitedHealthcare',
      member: 'LC-6610-3324',
      estimate: [{ label: 'Perio maint.', cdt: 'D4910', mode: 'pending', fee: 160, allowed: 136, insurance: 0, patient: 0 }],
      estimateNote: 'Amounts appear when the benefits check finishes — usually within the hour.',
    }),
  ],
  // ---- Next week (the rcm-23 Week scene) ----
  [NEXT_WEEK_MONDAY]: [
    // Mon Oct 19 — 1 not ready: Rosa Diaz
    featured({
      dateIso: NEXT_WEEK_MONDAY,
      providerId: 'carter',
      startMin: 480,
      lengthMin: 90,
      patient: 'Rosa Diaz',
      age: 52,
      since: 2017,
      dob: '04/08/1974',
      chart: '20560',
      procedure: { label: 'Crown #8', cdt: 'D2740' },
      state: 'not_ready',
      line: 'Pre-auth: no answer yet',
      plan: 'Aetna PPO',
      payer: 'Aetna',
      member: 'RD-8891-2210',
      checkedAt: '2026-10-15T06:10:00',
      sources: 3,
      yearlyMaxLeft: 900,
      yearlyMax: 1500,
      ortho: { left: 1500, max: 1500 },
      deductible: { label: 'Met', sub: 'met for 2026' },
      estimate: [{ label: 'Crown #8', cdt: 'D2740', mode: 'pending', fee: 1150, allowed: 960, insurance: 0, patient: 0 }],
      estimateNote: 'Insurance is "not yet known" until Aetna answers the pre-auth.',
      notReady: {
        heading: "Aetna hasn't answered the pre-auth",
        detail: 'Sent Oct 9. No answer yet — it may not come back before the visit.',
        link: PREAUTH_LINK,
      },
      known: { kind: 'not_ready', reason: 'Pre-auth sent Oct 9 · no answer yet' },
      opportunities: [
        {
          id: 'max-unused',
          name: "$900 of this year's maximum is unused",
          line: 'Resets Jan 1. The crown would use part of it.',
          kind: 'worth',
          pool: 'none',
          amount: 900,
          notNow: 'Waits for the pre-auth answer.',
        },
      ],
    }),
  ],
  [addDaysIso(NEXT_WEEK_MONDAY, 1)]: [
    // Tue Oct 20 — 2 not ready: Orville Allison, Patricia Levin
    featured({
      dateIso: addDaysIso(NEXT_WEEK_MONDAY, 1),
      providerId: 'moreno',
      startMin: 630,
      lengthMin: 60,
      patient: 'Orville Allison',
      age: 49,
      since: 2021,
      dob: '05/19/1977',
      chart: '21055',
      procedure: { label: 'Cleaning', cdt: 'D1110' },
      state: 'not_ready',
      line: 'No insurance on file',
      plan: null,
      member: null,
      estimate: [{ label: 'Cleaning', cdt: 'D1110', mode: 'pending', fee: 120, allowed: 102, insurance: 0, patient: 0 }],
      estimateNote: 'No plan on file — ELVA has nothing to verify.',
      notReady: {
        heading: 'No insurance on file',
        detail: "ELVA couldn't find a plan to check. A teammate can add insurance, or confirm the visit is self-pay.",
        link: HITL_LINK,
      },
      known: { kind: 'not_ready', reason: 'No insurance on file' },
    }),
    featured({
      dateIso: addDaysIso(NEXT_WEEK_MONDAY, 1),
      providerId: 'poe',
      startMin: 840,
      lengthMin: 90,
      patient: 'Patricia Levin',
      age: 58,
      since: 2016,
      dob: '09/02/1968',
      chart: '18740',
      procedure: { label: 'Crown #30', cdt: 'D2740' },
      state: 'not_ready',
      line: 'Coverage ended Sep 30',
      plan: 'Delta Dental PPO',
      payer: 'Delta Dental',
      member: 'PL-2290-7781',
      checkedAt: '2026-10-15T06:10:00',
      sources: 3,
      planYear: null,
      coverageEnded: 'Sep 30, 2025',
      estimate: [{ label: 'Crown #30', cdt: 'D2740', mode: 'ended', fee: 1150, allowed: 960, insurance: 0, patient: 0 }],
      notReady: {
        heading: 'Her Delta Dental plan ended September 30',
        detail: 'The last check found no active coverage. She may have a new plan — a teammate can ask before the visit.',
        link: HITL_LINK,
      },
      known: { kind: 'not_ready', reason: 'Coverage ended Sep 30' },
    }),
  ],
  [addDaysIso(NEXT_WEEK_MONDAY, 2)]: [
    // Wed Oct 21 — 1 not ready: Teresa Nguyen
    featured({
      dateIso: addDaysIso(NEXT_WEEK_MONDAY, 2),
      providerId: 'carter',
      startMin: 780,
      lengthMin: 90,
      patient: 'Teresa Nguyen',
      age: 55,
      since: 2013,
      dob: '12/15/1970',
      chart: '20774',
      procedure: { label: 'Implant #19', cdt: 'D6010' },
      state: 'not_ready',
      line: 'Pre-auth: no answer in time',
      plan: 'MetLife PDP+',
      payer: 'MetLife',
      member: 'TN-4412-8890',
      checkedAt: '2026-10-15T06:10:00',
      sources: 4,
      yearlyMaxLeft: 1300,
      yearlyMax: 1500,
      ortho: { left: 1500, max: 1500 },
      deductible: { label: 'Met', sub: 'met for 2026' },
      estimate: [{ label: 'Implant #19', cdt: 'D6010', mode: 'pending', fee: 2400, allowed: 1650, insurance: 0, patient: 0 }],
      estimateNote: 'Insurance is "not yet known" until MetLife answers the pre-auth.',
      notReady: {
        heading: "MetLife won't answer in time",
        detail: "MetLife's pre-auths take about 3 weeks; this one won't come back before Oct 21.",
        link: PREAUTH_LINK,
      },
      known: { kind: 'not_ready', reason: "Pre-auth: MetLife won't answer in time" },
    }),
  ],
  [addDaysIso(NEXT_WEEK_MONDAY, 4)]: [
    // Fri Oct 23 — 0 not ready, 1 worth knowing: Daniel Foster
    featured({
      dateIso: addDaysIso(NEXT_WEEK_MONDAY, 4),
      providerId: 'carter',
      startMin: 660,
      lengthMin: 90,
      patient: 'Daniel Foster',
      age: 47,
      since: 2020,
      dob: '08/30/1979',
      chart: '20318',
      procedure: { label: 'Implant #9', cdt: 'D6010' },
      state: 'ready',
      line: '✓ Covered · patient $600',
      plan: 'Guardian PPO',
      payer: 'Guardian',
      member: 'DF-5540-1123',
      checkedAt: '2026-10-15T06:10:00',
      sources: 4,
      // Unused for 2026: the approved $1,800 share has to fit under what is left.
      yearlyMaxLeft: 2000,
      yearlyMax: 2000,
      deductible: { label: 'Met', sub: 'met for 2026' },
      estimate: [{ label: 'Implant #9', cdt: 'D6010', mode: 'known', fee: 2400, allowed: 2400, insurance: 1800, patient: 600 }],
      estimateNote: 'The pre-auth is approved. It expires Nov 2, so keep the visit on or before then.',
      known: { kind: 'worth_knowing', reason: 'Approved · expires Nov 2' },
    }),
  ],
};

/** Exact visit counts per day, so the band headlines line up with the handoff. */
const DAY_TARGET: Record<string, number> = {
  [THIS_WEEK_MONDAY]: 22,
  [addDaysIso(THIS_WEEK_MONDAY, 1)]: 20,
  [addDaysIso(THIS_WEEK_MONDAY, 2)]: 25,
  [addDaysIso(THIS_WEEK_MONDAY, 3)]: 23,
  [MOCK_TODAY_ISO]: 23,
  [NEXT_WEEK_MONDAY]: 24,
  [addDaysIso(NEXT_WEEK_MONDAY, 1)]: 21,
  [addDaysIso(NEXT_WEEK_MONDAY, 2)]: 26,
  [addDaysIso(NEXT_WEEK_MONDAY, 3)]: 19,
  [addDaysIso(NEXT_WEEK_MONDAY, 4)]: 17,
};

/** Reserve the 30-min sub-slots a visit of `lengthMin` occupies from `startMin`. */
function reserve(set: Set<number>, startMin: number, lengthMin: number): void {
  for (let m = startMin; m < startMin + lengthMin; m += 30) set.add(m);
}

/** The first slot where a visit of `lengthMin` fits without overlapping. */
function freeSlot(set: Set<number>, lengthMin: number): number | undefined {
  const need = Math.ceil(lengthMin / 30);
  return SLOTS.find((start) => {
    for (let k = 0; k < need; k++) {
      if (set.has(start + k * 30)) return false;
      if (start + k * 30 >= GRID_END_MIN) return false;
    }
    return true;
  });
}

function buildDay(dateIso: string): Visit[] {
  const target = DAY_TARGET[dateIso] ?? 0;
  if (target === 0) return [];
  const r = rng(seedFromIso(dateIso));
  const taken: Record<string, Set<number>> = {};
  const seeds = FEATURED[dateIso] ?? [];
  const visits: Visit[] = seeds.map((s, i) => {
    reserve((taken[s.providerId] ??= new Set()), s.startMin, s.lengthMin);
    return { ...s, id: `v-${dateIso}-feat-${String(i)}` };
  });

  let i = 0;
  let guard = 0;
  while (visits.length < target && guard < 800) {
    guard++;
    const prov = PROVIDERS[i % PROVIDERS.length];
    i++;
    if (!prov) continue;
    const set = (taken[prov.id] ??= new Set());
    const len = r() < 0.18 ? 90 : 60;
    const slot = freeSlot(set, len);
    if (slot === undefined) continue;
    reserve(set, slot, len);
    visits.push(makeFiller(r, dateIso, prov.id, slot, len, visits.length));
  }

  return visits.sort((a, b) => a.startMin - b.startMin || a.providerId.localeCompare(b.providerId));
}

/** The whole mock schedule, built once, keyed by ISO date. */
const SCHEDULE: Record<string, Visit[]> = {};
for (const iso of Object.keys(DAY_TARGET)) {
  SCHEDULE[iso] = buildDay(iso);
}

/* ----------------------------------------------------------------------------
 * Selectors.
 * -------------------------------------------------------------------------- */

export function visitsForDate(iso: string): Visit[] {
  return SCHEDULE[iso] ?? [];
}

/** A person the schedule names: a patient on a visit, or a family member on a plan. */
export interface SchedulePerson {
  name: string;
  /** Chart number: the one key the schedule and the directory share. */
  chart: string;
  /** US date, mm/dd/yyyy; absent for family members, whose birth date the schedule doesn't hold. */
  dob?: string;
}

/** Everyone the schedule names, once per chart, in a fixed order. The directory gives each a record. */
export function schedulePeople(): SchedulePerson[] {
  const seen = new Set<string>();
  const out: SchedulePerson[] = [];
  const add = (p: SchedulePerson) => {
    if (seen.has(p.chart)) return;
    seen.add(p.chart);
    out.push(p);
  };
  for (const iso of Object.keys(SCHEDULE).sort()) {
    for (const v of SCHEDULE[iso] ?? []) {
      add({ name: v.patient, chart: v.chart, dob: v.dob });
      for (const m of v.family) add({ name: m.name, chart: m.chart });
    }
  }
  return out;
}

export function visitById(id: string): Visit | null {
  for (const day of Object.values(SCHEDULE)) {
    const hit = day.find((v) => v.id === id);
    if (hit) return hit;
  }
  return null;
}

/** Providers (in PMS order) that have a visit that day, with their visit count. */
export function providerColumnsForDate(iso: string): { provider: Provider; count: number }[] {
  const visits = visitsForDate(iso);
  return PROVIDERS.map((p) => ({ provider: p, count: visits.filter((v) => v.providerId === p.id).length })).filter(
    (c) => c.count > 0,
  );
}

/** Day-view "not ready" count: visits in the not_ready state (after the check). */
export function dayNotReadyCount(iso: string, providerId?: string): number {
  return visitsForDate(iso).filter(
    (v) => v.state === 'not_ready' && (!providerId || providerId === 'all' || v.providerId === providerId),
  ).length;
}

export function dayVisitCount(iso: string, providerId?: string): number {
  return visitsForDate(iso).filter((v) => !providerId || providerId === 'all' || v.providerId === providerId).length;
}

/** Week-view "known now not ready" count — what ELVA knows before the day-before check. */
export function knownNotReadyCount(iso: string, providerId?: string): number {
  return visitsForDate(iso).filter(
    (v) => v.known?.kind === 'not_ready' && (!providerId || providerId === 'all' || v.providerId === providerId),
  ).length;
}

/** Mon–Fri of a week (plus Sat/Sun only if they carry visits — none do in the mock). */
export function weekdaysOf(mondayIso: string): string[] {
  const days: string[] = [];
  for (let d = 0; d < 5; d++) days.push(addDaysIso(mondayIso, d));
  for (let d = 5; d < 7; d++) {
    const iso = addDaysIso(mondayIso, d);
    if (visitsForDate(iso).length > 0) days.push(iso);
  }
  return days;
}

export function weekVisitCount(mondayIso: string, providerId?: string): number {
  return weekdaysOf(mondayIso).reduce((sum, iso) => sum + dayVisitCount(iso, providerId), 0);
}

export function weekKnownNotReadyCount(mondayIso: string, providerId?: string): number {
  return weekdaysOf(mondayIso).reduce((sum, iso) => sum + knownNotReadyCount(iso, providerId), 0);
}

/** The next date on or after `iso` that has visits (for the empty-day line). */
export function nextVisitDateOnOrAfter(iso: string): string | null {
  const dates = Object.keys(SCHEDULE).sort();
  for (const d of dates) {
    if (d >= iso && d !== iso && visitsForDate(d).length > 0) return d;
  }
  return null;
}

/**
 * What the opportunities pane shows (D27, D47): age limits and what the plan
 * has left are applied first, so nothing offered is one the patient can't use.
 * Plan amounts are capped by their pool; the list is ordered by worth.
 */
export function visibleOpportunities(visit: Visit): VisitOpportunity[] {
  const shown: VisitOpportunity[] = [];
  for (const o of visit.opportunities) {
    if (o.ageMin !== undefined && visit.age < o.ageMin) continue;
    if (o.ageMax !== undefined && visit.age > o.ageMax) continue;
    if (o.kind === 'plan') {
      const left = o.pool === 'ortho' ? (visit.ortho?.left ?? null) : visit.yearlyMaxLeft;
      if (left === null || left <= 0) continue;
      shown.push({ ...o, amount: Math.min(o.amount, left) });
    } else {
      shown.push(o);
    }
  }
  return shown.sort((a, b) => b.amount - a.amount);
}
